import { I18nProvider, LoginPage as OmniLoginPage } from "@omnitok/ui";
import { OMNITOK_LOGIN_PT } from "@pervasivemind/dashboard-render";
import { useState } from "react";
import { Navigate, useLocation, type Location } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";
import {
  HttpError,
  sessionExpiredMessage,
  wasSessionRecentlyExpired,
} from "@/lib/api";

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const { user, login } = useAuth();
  const { t, locale } = useT();
  const location = useLocation() as Location & { state: LocationState | null };
  const from = location.state?.from ?? "/";

  const [submitting, setSubmitting] = useState(false);
  // Landed here because a 401 kicked the user out? Say so, instead of
  // showing a bare form. Read-and-cleared once on first render.
  const [error, setError] = useState<string | undefined>(() =>
    wasSessionRecentlyExpired() ? sessionExpiredMessage() : undefined,
  );

  if (user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (email: string, password: string): Promise<void> => {
    setError(undefined);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) {
        setError(t("login.invalid"));
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t("common.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Match the portal's split-panel branding (`hp-portal/frontend/src/pages/
  // LoginPage.tsx`) — same SVG, same hero treatment. The previous version
  // rendered a hand-rolled "AI" square in `bg-primary` which read as
  // placeholder. Using the brand SVG keeps the builder consistent with
  // the portal and the rest of the Omnitok-skinned stack.
  const logo = (
    <img
      src="/omnitok-logo.svg"
      alt="Digital Shelf Analytics"
      className="h-10 w-auto"
      fetchPriority="high"
    />
  );

  const heroLogo = (
    <img
      src="/omnitok-logo.svg"
      alt="Digital Shelf Analytics"
      className="h-24 w-auto drop-shadow-lg"
    />
  );

  // The kit's `language` prop is 'es' | 'en'; the language goes through
  // its I18nProvider instead, which also carries the PT copy.
  return (
    <I18nProvider language={locale} messages={{ pt: OMNITOK_LOGIN_PT }}>
      <OmniLoginPage
        logo={logo}
        logoWide={logo}
        splitLogo={heroLogo}
        onLoginSubmit={(email: string, password: string) => void handleSubmit(email, password)}
        loading={submitting}
        error={error}
        showRememberMe={false}
        showForgotPassword={false}
      />
    </I18nProvider>
  );
}
